/**
 * Calcoli di composizione corporea basati su plicometria
 *
 * Le plicometrie devono essere in millimetri (mm)
 * Le formule richiedono: plicometrie, età, sesso, peso (per massa grassa/magra)
 */

/**
 * Converte/Valida input in millimetri
 * (Assume che l'input sia già in mm, fa solo parsing)
 */
const toMm = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const num = parseFloat(val);
  return isNaN(num) ? null : num;
};

// Helper per normalizzare il sesso
const isMale = (gender) => {
  if (!gender) return false;
  const g = gender.toString().toLowerCase().trim();
  return g === 'm' || g === 'maschio' || g === 'uomo' || g.startsWith('m');
};

const isFemale = (gender) => {
  if (!gender) return false;
  const g = gender.toString().toLowerCase().trim();
  return g === 'f' || g === 'femmina' || g === 'donna' || g.startsWith('f');
};

/**
 * Calcola la densità corporea con Jackson-Pollock 7 pliche
 * @param {Object} measurements - Oggetto con le plicometrie in mm
 * @param {number} age - Età in anni
 * @param {string} gender - 'M' per maschio, 'F' per femmina
 * @returns {number|null} Densità corporea in g/cm³
 */
export const calculateJacksonPollock7 = (measurements, age, gender) => {
  if (!age || !gender) return null;

  const pettorale = toMm(measurements.pettorale);
  const ascellare = toMm(measurements.ascellare);
  const tricipite = toMm(measurements.tricipite);
  const soprailiaca = toMm(measurements.soprailiaca);
  const addominale = toMm(measurements.addominale);
  const coscia = toMm(measurements.coscia);
  const bicipite = toMm(measurements.bicipite);

  // Verifica che tutte le 7 pliche siano disponibili
  if (pettorale === null || ascellare === null || tricipite === null || soprailiaca === null || addominale === null || coscia === null || bicipite === null) {
    return null;
  }

  const sum = pettorale + ascellare + tricipite + soprailiaca + addominale + coscia + bicipite;
  const sumSquared = sum * sum;

  let density;
  if (isMale(gender)) {
    // Formula per uomini (JP7 standard per somma in mm)
    density = 1.112 - (0.00043499 * sum) + (0.00000055 * sumSquared) - (0.00028826 * age);
  } else if (isFemale(gender)) {
    // Formula per donne (JP7 standard per somma in mm)
    density = 1.097 - (0.00046971 * sum) + (0.00000056 * sumSquared) - (0.00012828 * age);
  } else {
    return null;
  }

  return density;
};

/**
 * Calcola la densità corporea con Jackson-Pollock 3 pliche
 * @param {Object} measurements - Oggetto con le plicometrie in mm
 * @param {number} age - Età in anni
 * @param {string} gender - 'M' per maschio, 'F' per femmina
 * @returns {number|null} Densità corporea in g/cm³
 */
export const calculateJacksonPollock3 = (measurements, age, gender) => {
  if (!age || !gender) return null;

  let sum;
  let sumSquared; // Dichiarazione esplicita qui

  if (isMale(gender)) {
    // Uomini: pettorale, addominale, coscia
    const pettorale = toMm(measurements.pettorale);
    const addominale = toMm(measurements.addominale);
    const coscia = toMm(measurements.coscia);

    if (pettorale === null || addominale === null || coscia === null) return null;
    sum = pettorale + addominale + coscia;
    sumSquared = sum * sum; 
  } else if (isFemale(gender)) {
    // La logica per le donne è gestita nel blocco successivo
  } else {
    return null;
  }

  // sumSquared viene calcolato dentro i blocchi specifici ora
  // const sumSquared = sum * sum; // Rimosso perché calcolato dinamicamente

  let density;
  if (isMale(gender)) {
    // Formula per uomini (JP3 standard per somma in mm)
    // D = 1.10938 - (0.0008267 * sum) + (0.0000016 * sum^2) - (0.0002574 * age)
    density = 1.10938 - (0.0008267 * sum) + (0.0000016 * sumSquared) - (0.0002574 * age);
  } else if (isFemale(gender)) {
    // Donne: tricipite, soprailiaca, coscia
    const tricipite = toMm(measurements.tricipite);
    const soprailiaca = toMm(measurements.soprailiaca);
    const coscia = toMm(measurements.coscia);
    
    // Supporto per siti "Maschili" su donne (Petto, Addome, Coscia) se specificato dall'utente
    const pettorale = toMm(measurements.pettorale);
    const addominale = toMm(measurements.addominale);

    if (tricipite !== null && soprailiaca !== null && coscia !== null) {
      // Formula Standard Donne (Tricipite, Soprailiaca, Coscia)
      // D = 1.0994921 - (0.0009929 * sum) + (0.0000023 * sum^2) - (0.0001392 * age)
      sum = tricipite + soprailiaca + coscia;
      sumSquared = sum * sum;
      density = 1.0994921 - (0.0009929 * sum) + (0.0000023 * sumSquared) - (0.0001392 * age);
    } else if (pettorale !== null && addominale !== null && coscia !== null) {
      // Fallback: Formula Uomini applicata a siti Uomini su Donne (Richiesta specifica utente)
      // Nota: Questa non è standard scientifico ma permette il calcolo se misurati questi siti
      sum = pettorale + addominale + coscia;
      sumSquared = sum * sum;
      // Usiamo la formula maschio come approssimazione per questi siti specifici, 
      // o potremmo cercare una formula specifica donna per questi siti (rara).
      // Per ora usiamo formula maschio avvisando implicitamente.
      density = 1.10938 - (0.0008267 * sum) + (0.0000016 * sumSquared) - (0.0002574 * age);
    } else {
      return null;
    }
  } else {
    return null;
  }

  return density;
};

/**
 * Calcola la densità corporea con Durnin-Womersley 4 pliche
 * @param {Object} measurements - Oggetto con le plicometrie in mm
 * @param {number} age - Età in anni
 * @param {string} gender - 'M' per maschio, 'F' per femmina
 * @returns {number|null} Densità corporea in g/cm³
 */
export const calculateDurninWomersley = (measurements, age, gender) => {
  if (!age || !gender) return null;

  const bicipite = toMm(measurements.bicipite);
  const tricipite = toMm(measurements.tricipite);
  const scapolare = toMm(measurements.scapolare);
  const soprailiaca = toMm(measurements.soprailiaca);

  // Verifica che tutte le 4 pliche siano disponibili
  if (bicipite === null || tricipite === null || scapolare === null || soprailiaca === null) {
    return null;
  }

  const sum = bicipite + tricipite + scapolare + soprailiaca;
  if (sum <= 0) return null;
  
  const logSum = Math.log10(sum);

  // Coefficienti Durnin-Womersley (standard per somma in mm)
  let a, b;

  if (isMale(gender)) {
    if (age < 17) {
        a = 1.1533; b = 0.0643;
    } else if (age >= 17 && age <= 19) {
      a = 1.1620; b = 0.0630;
    } else if (age >= 20 && age <= 29) {
      a = 1.1631; b = 0.0632;
    } else if (age >= 30 && age <= 39) {
      a = 1.1422; b = 0.0544;
    } else if (age >= 40 && age <= 49) {
      a = 1.1620; b = 0.0700;
    } else if (age >= 50) {
      a = 1.1715; b = 0.0779;
    }
  } else if (isFemale(gender)) {
    if (age < 17) {
        a = 1.1369; b = 0.0598;
    } else if (age >= 17 && age <= 19) {
      a = 1.1549; b = 0.0678;
    } else if (age >= 20 && age <= 29) {
      a = 1.1599; b = 0.0717;
    } else if (age >= 30 && age <= 39) {
      a = 1.1423; b = 0.0632;
    } else if (age >= 40 && age <= 49) {
      a = 1.1333; b = 0.0612;
    } else if (age >= 50) {
      a = 1.1339; b = 0.0645;
    }
  } else {
    return null;
  }
  
  if (!a || !b) return null;

  const density = a - (b * logSum);
  return density;
};

/**
 * Calcola la percentuale di grasso corporeo dalla densità (formula Siri)
 * @param {number} density - Densità corporea in g/cm³
 * @returns {number|null} Percentuale di grasso corporeo
 */
export const calculateBodyFatPercentage = (density) => {
  if (!density || density <= 0) return null;

  // Formula Siri: %BF = (495 / D) - 450
  const bodyFat = (495 / density) - 450;

  // Rimosso il cap limitante (0-60) per maggiore precisione su valori estremi
  return bodyFat;
};

/**
 * Calcola la massa grassa in kg
 * @param {number} bodyFatPercentage - Percentuale di grasso corporeo
 * @param {number} weight - Peso corporeo in kg
 * @returns {number|null} Massa grassa in kg
 */
export const calculateFatMass = (bodyFatPercentage, weight) => {
  if (bodyFatPercentage === null || !weight || weight <= 0) return null;
  return (bodyFatPercentage / 100) * weight;
};

/**
 * Calcola la massa magra in kg
 * @param {number} fatMass - Massa grassa in kg
 * @param {number} weight - Peso corporeo in kg
 * @returns {number|null} Massa magra in kg
 */
export const calculateLeanMass = (fatMass, weight) => {
  if (fatMass === null || !weight || weight <= 0) return null;
  return weight - fatMass;
};

/**
 * Calcola tutti gli indici corporei usando i tre metodi
 * @param {Object} measurement - Oggetto con le plicometrie in mm
 * @param {number} age - Età in anni
 * @param {string} gender - 'M' per maschio, 'F' per femmina
 * @param {number} weight - Peso corporeo in kg
 * @returns {Object} Oggetto con tutti i calcoli
 */
export const calculateAllBodyComposition = (measurement, age, gender, weight) => {
  console.log('calculateAllBodyComposition input:', { measurement, age, gender, weight });

  const results = {
    jacksonPollock7: null,
    jacksonPollock3: null,
    durninWomersley: null,
  };

  // Jackson-Pollock 7 pliche
  const density7 = calculateJacksonPollock7(measurement, age, gender);
  if (density7) {
    const bf7 = calculateBodyFatPercentage(density7);
    const fatMass = calculateFatMass(bf7, weight);
    results.jacksonPollock7 = {
      density: density7,
      bodyFatPercentage: bf7,
      fatMass: fatMass,
      leanMass: calculateLeanMass(fatMass, weight),
    };
  }

  // Jackson-Pollock 3 pliche
  const density3 = calculateJacksonPollock3(measurement, age, gender);
  if (density3) {
    const bf3 = calculateBodyFatPercentage(density3);
    const fatMass = calculateFatMass(bf3, weight);
    results.jacksonPollock3 = {
      density: density3,
      bodyFatPercentage: bf3,
      fatMass: fatMass,
      leanMass: calculateLeanMass(fatMass, weight),
    };
  }

  // Durnin-Womersley 4 pliche
  const densityDW = calculateDurninWomersley(measurement, age, gender);
  if (densityDW) {
    const bfDW = calculateBodyFatPercentage(densityDW);
    const fatMass = calculateFatMass(bfDW, weight);
    results.durninWomersley = {
      density: densityDW,
      bodyFatPercentage: bfDW,
      fatMass: fatMass,
      leanMass: calculateLeanMass(fatMass, weight),
    };
  }

  console.log('calculateAllBodyComposition output:', results);
  return results;
};